import React, { useState, useEffect } from "react"
import Header from "components/Header/Header"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { Container } from "react-bootstrap"
import {
  Typography,
  Box,
  styled,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Grid,
  useTheme,
  Tooltip
} from "@mui/material"
import { TabContext, TabList } from "@mui/lab"
import styles from "./engagementAnalytics.module.scss"
import Card from "components/Card/Card"
import { useDispatch, useSelector } from "react-redux"
import {
  getEngagementAnalyticsAction,
  exportEngagementAnalyticsAction
} from "redux/DMV/actions/user"
import { appActions } from "redux/app/slice/app"
import TableNoRecords from "components/TableNoRecords"
import { cards, metrics, summaryColumns, tableColumnsMap, tabs } from "helpers/constant"
import AppPagination from "components/StyledComponents/AppPagination"
import AppButton from "components/StyledComponents/AppButton"
import downloadIcon from "assets/icons/Download.svg"
import InfoIcon from "@mui/icons-material/InfoOutlined"

const CustomTabList = styled(TabList)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.aqua.main,
    height: "4px"
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.aqua.main
  },
  "& .MuiTab-root": {
    textTransform: "none",
    padding: "20px 28px 20px 28px"
  },
  "& .MuiTabs-scroller ": {
    marginLeft: "12px"
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    padding: "24px 24px 24px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: `2px solid ${theme.palette.background.default}`,
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.background.main}`
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "relative",
    padding: "24px 24px 24px 0px",
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const EngagementAnalytics = () => {
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [activeTab, setActiveTab] = useState("summary")
  const [currentPage, setCurrentPage] = useState(1)
  const { startDate, endDate, restaurant } = useSelector((store) => store?.app?.filters || {})
  const { isLoading } = useSelector((store) => store?.app || {})
  const { engagementAnalytics } = useSelector((store) => store?.dmv)
  const getColumnsForTab = (tab) => tableColumnsMap[tab] || summaryColumns
  const theme = useTheme()
  const dispatch = useDispatch()

  const currentColumns = getColumnsForTab(activeTab)
  const currentRows =
    activeTab === "summary"
      ? metrics?.map((item) => {
          const value = engagementAnalytics?.[item.key]

          if (!value) {
            return {
              metrics: item.label,
              count: "-",
              numerator: "-",
              denominator: "-",
              percentage: "-",
              isTooltip: item.isTooltip,
              tooltip: item.tooltip
            }
          }

          if (typeof value === "number") {
            return {
              metrics: item.label,
              count: value,
              numerator: "-",
              denominator: "-",
              percentage: "-",
              isTooltip: item.isTooltip,
              tooltip: item.tooltip
            }
          }

          return {
            metrics: item.label,
            count: value?.count ?? "-",
            numerator: value?.numerator ?? "-",
            denominator: value?.denominator ?? "-",
            percentage: value?.percentage ?? "-",
            isTooltip: item.isTooltip,
            tooltip: item.tooltip
          }
        })
      : engagementAnalytics?.results?.users?.map((user, index) => ({
          idx: currentPage * 12 - (12 - 1) + index,
          name: user.full_name,
          email: user.email
        })) || []

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)
    setCurrentPage(1)
  }
  const handleExport = () => {
    dispatch(
      exportEngagementAnalyticsAction({
        startDate: startDate,
        endDate: endDate,
        restaurant: restaurant,
        tab: activeTab
      })
    )
  }

  useEffect(() => {
    dispatch(
      getEngagementAnalyticsAction({
        startDate: startDate,
        endDate: endDate,
        restaurant: restaurant,
        tab: activeTab,
        page: currentPage
      })
    )
  }, [startDate, endDate, restaurant, activeTab, currentPage])

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs())
    document.title = "Engagement Analytics"
    dispatch(appActions.initFilters())
  }, [])

  return (
    <>
      <Header title="Engagement Analytics" breadCrumbs={breadCrumbs} back={true}>
        <AppButton styles={{ padding: "13px 30px" }} onClick={handleExport}>
          <img src={downloadIcon} style={{ marginRight: "8px" }} />
          Export
        </AppButton>
      </Header>
      {/* <Box> */}
      <TabContext value={activeTab}>
        <Box>
          <CustomTabList onChange={handleChange} sx={{ overflow: "auto" }}>
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} value={tab.value} />
            ))}
          </CustomTabList>
        </Box>
      </TabContext>
      {/* </Box> */}
      {activeTab !== "summary" && (
        <Grid container spacing={2} className="px-4 py-4">
          {cards.map((card) => {
            const result = engagementAnalytics?.results || {}

            const value =
              card.value === "count"
                ? engagementAnalytics?.count ?? "-"
                : card.value === "percentage"
                ? result.percentage
                  ? `${result.percentage}%`
                  : "-"
                : result[card.value] ?? "-"

            return (
              <Grid item xs={3} key={card.value} className="d-flex">
                <Card className={styles.card}>
                  <Box className="d-flex flex-column gap-4">
                    <Typography variant="body2Regular" color={theme.palette.text.secondary}>
                      {card.label}
                    </Typography>

                    <Typography variant="h3" color={theme.palette.aqua.main}>
                      {value}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}

      <Container fluid className="mt-3">
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
          <CustomTable>
            <TableHead>
              <TableRow>
                {currentColumns?.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent={column.align === "center" ? "center" : "flex-start"}>
                      <Typography variant="body2Regular" sx={{ opacity: 0.6 }}>
                        {column.label}
                      </Typography>
                      {column.tooltip ? (
                        <Tooltip title={column.tooltip} placement="top">
                          <InfoIcon fontSize="small" sx={{ opacity: 0.5 }} />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows?.length > 0 ? (
                currentRows?.map((row, index) => (
                  <TableRow key={index}>
                    {activeTab === "summary" ? (
                      <>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2Regular">{row?.metrics ?? "-"}</Typography>
                            {row?.isTooltip && row?.tooltip ? (
                              <Tooltip title={row.tooltip} placement="top">
                                <InfoIcon fontSize="small" sx={{ opacity: 0.5 }} />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">{row?.count ?? "-"}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">{row?.numerator ?? "-"}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">{row?.denominator ?? "-"}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">
                            {row?.percentage !== "-" ? `${row?.percentage} %` : "-"}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center">
                          <Typography variant="body2Regular">{row?.idx ?? "-"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2Regular">{row?.name ?? "-"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2Regular">{row?.email ?? "-"}</Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableNoRecords
                  colSpan={currentColumns?.length}
                  label={
                    isLoading
                      ? "Please wait, we are fetching the engagement analytics..."
                      : "No Data Found"
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Container>
      {activeTab !== "summary" && (
        <AppPagination
          totalPages={engagementAnalytics?.pages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}
    </>
  )
}

export default EngagementAnalytics
